/**
 * Monitoring — National role screen (formerly Alarms & Compliance)
 * FR-N18 to FR-N19
 */
import React, { useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import KPICard from '../../components/shared/KPICard';
import SectionDivider from '../../components/shared/SectionDivider';
import {
  nationalKPIs,
  stateMonitoringData,
  discomMonitoringData,
  districtMonitoringData,
  circleMonitoringData,
} from '../../data/mockData';

type SortKey = 'state' | 'district' | 'discom' | 'circle' | 'dataTxPercent' | 'plantsOffline' | 'activeAlarms' | 'certsExpiring' | 'firmwareOutdated';
type SortDir  = 'asc' | 'desc';

const txCls = (v: number) =>
  v >= 90 ? 'text-green-600 font-semibold' :
  v >= 70 ? 'text-amber-600 font-semibold' :
            'text-red-600 font-semibold';

const offlineCls  = (v: number) => v > 0 ? 'text-red-600 font-semibold' : 'text-text-secondary';
const alarmCls    = (v: number) => v > 0 ? 'text-red-600 font-semibold' : 'text-text-secondary';
const certCls     = (v: number) => v > 0 ? 'text-amber-600 font-semibold' : 'text-text-secondary';

function SortTh({
  label, col, sortCol, sortDir, onClick, right,
}: {
  label: string; col: SortKey; sortCol: SortKey; sortDir: SortDir;
  onClick: (c: SortKey) => void; right?: boolean;
}) {
  return (
    <th
      onClick={() => onClick(col)}
      className={`py-2.5 px-3 text-text-secondary font-semibold cursor-pointer select-none hover:text-accent ${right ? 'text-right' : 'text-left'}`}
    >
      {label}{sortCol === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
    </th>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function sortRows<T>(rows: T[], col: string, dir: SortDir): T[] {
  return [...rows].sort((a, b) => {
    const av = (a as any)[col] as number | string;
    const bv = (b as any)[col] as number | string;
    if (av < bv) return dir === 'asc' ? -1 : 1;
    if (av > bv) return dir === 'asc' ? 1 : -1;
    return 0;
  });
}

export default function NationalCompliance() {
  // State compliance table
  const [stSort, setStSort]                   = useState<SortKey>('dataTxPercent');
  const [stDir,  setStDir]                    = useState<SortDir>('asc');
  const [stSelectedState, setStSelectedState] = useState<string | null>(null);

  // DISCOM compliance table
  const [dcSort, setDcSort]                       = useState<SortKey>('dataTxPercent');
  const [dcDir,  setDcDir]                        = useState<SortDir>('asc');
  const [dcSelectedDISCOM, setDcSelectedDISCOM]   = useState<string | null>(null);

  const stateNames  = Array.from(new Set(stateMonitoringData.map(r => r.state)));
  const discomNames = Array.from(new Set(discomMonitoringData.map(r => r.discom)));

  const selectState = (s: string | null) => {
    setStSelectedState(s);
    setStSort('dataTxPercent');
    setStDir('asc');
  };
  const selectDISCOM = (d: string | null) => {
    setDcSelectedDISCOM(d);
    setDcSort('dataTxPercent');
    setDcDir('asc');
  };

  const toggleStateSort = (col: SortKey) => {
    if (stSort === col) setStDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setStSort(col); setStDir('asc'); }
  };
  const toggleDcSort = (col: SortKey) => {
    if (dcSort === col) setDcDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setDcSort(col); setDcDir('asc'); }
  };

  // State table: states or districts
  const stTableRows = useMemo(() => {
    if (stSelectedState) {
      const rows = districtMonitoringData.filter(r => r.state === stSelectedState);
      const col  = stSort === 'state' ? 'district' : stSort;
      return sortRows(rows, col, stDir);
    }
    return sortRows(stateMonitoringData, stSort, stDir);
  }, [stSort, stDir, stSelectedState]);

  // DISCOM table: DISCOMs or circles
  const dcTableRows = useMemo(() => {
    if (dcSelectedDISCOM) {
      const rows = circleMonitoringData.filter(r => r.discom === dcSelectedDISCOM);
      const col  = dcSort === 'discom' ? 'circle' : dcSort;
      return sortRows(rows, col, dcDir);
    }
    return sortRows(discomMonitoringData, dcSort, dcDir);
  }, [dcSort, dcDir, dcSelectedDISCOM]);

  // First-column sort key adapts to current view
  const stNameCol: SortKey = stSelectedState   ? 'district' : 'state';
  const dcNameCol: SortKey = dcSelectedDISCOM  ? 'circle'   : 'discom';

  return (
    <div className="p-6 space-y-6">

      {/* ── 5 KPI cards ── */}
      <div className="flex gap-4">
        <KPICard title="Active Alarms"         value={nationalKPIs.activeAlarms.toLocaleString()}          subtext="Across all states"          subtextColor="red"   bgColor="bg-red-50" />
        <KPICard title="Critical Alarms"        value={String(nationalKPIs.criticalAlarms)}                 subtext="Immediate action needed"    subtextColor="red"   bgColor="bg-red-50" />
        <KPICard title="Plants Offline"         value={nationalKPIs.offlineFault.toLocaleString()}          subtext="Right now"                  subtextColor="red"   bgColor="bg-red-50" />
        <KPICard title="Data Transmission"      value={`${nationalKPIs.dataTransmissionPercent}%`}          subtext="Above 95% threshold"        subtextColor="green" />
        <KPICard title="Certs Expiring ≤30d"    value={String(nationalKPIs.certsExpiring)}                  subtext="Urgent renewal needed"      subtextColor="amber" bgColor="bg-orange-50" />
      </div>

      {/* ── State-wise compliance table ── */}
      <div className="bg-white rounded-card border border-border shadow-card overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center gap-3">
          <SectionDivider
            title={stSelectedState ? `Districts — ${stSelectedState}` : 'State-wise Compliance'}
            subtitle="Default sort: data transmission % ascending (worst first)"
          />
          <div className="flex-1" />
          {stSelectedState ? (
            <div className="flex items-center gap-1 text-xs text-text-secondary">
              <button onClick={() => selectState(null)} className="text-accent hover:underline font-medium">All States</button>
              <ChevronRight size={11} className="text-text-tertiary" />
              <span className="font-medium text-text-primary">{stSelectedState}</span>
            </div>
          ) : (
            <select
              value=""
              onChange={e => { if (e.target.value) selectState(e.target.value); }}
              className="text-xs border border-border rounded-input px-2.5 py-1.5 bg-white focus:outline-none focus:border-accent"
            >
              <option value="">All States</option>
              {stateNames.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg-secondary border-b border-border">
              <tr>
                <SortTh label={stSelectedState ? 'District' : 'State'} col={stNameCol} sortCol={stSort} sortDir={stDir} onClick={toggleStateSort} />
                <SortTh label="Data Tx %"         col="dataTxPercent"    sortCol={stSort} sortDir={stDir} onClick={toggleStateSort} right />
                <SortTh label="Plants Offline"    col="plantsOffline"    sortCol={stSort} sortDir={stDir} onClick={toggleStateSort} right />
                <SortTh label="Active Alarms"     col="activeAlarms"     sortCol={stSort} sortDir={stDir} onClick={toggleStateSort} right />
                <SortTh label="Certs Expiring"    col="certsExpiring"    sortCol={stSort} sortDir={stDir} onClick={toggleStateSort} right />
                <SortTh label="Firmware Outdated" col="firmwareOutdated" sortCol={stSort} sortDir={stDir} onClick={toggleStateSort} right />
              </tr>
            </thead>
            <tbody>
              {stSelectedState ? (
                (stTableRows as typeof districtMonitoringData).map(row => (
                  <tr key={row.district} className="border-b border-border/50 hover:bg-bg-secondary transition-colors">
                    <td className="py-2.5 px-4 font-medium text-text-primary">{row.district}</td>
                    <td className={`py-2.5 px-3 text-right ${txCls(row.dataTxPercent)}`}>{row.dataTxPercent.toFixed(1)}%</td>
                    <td className={`py-2.5 px-3 text-right ${offlineCls(row.plantsOffline)}`}>{row.plantsOffline.toLocaleString()}</td>
                    <td className={`py-2.5 px-3 text-right ${alarmCls(row.activeAlarms)}`}>{row.activeAlarms.toLocaleString()}</td>
                    <td className={`py-2.5 px-3 text-right ${certCls(row.certsExpiring)}`}>{row.certsExpiring}</td>
                    <td className={`py-2.5 px-3 text-right ${certCls(row.firmwareOutdated)}`}>{row.firmwareOutdated}</td>
                  </tr>
                ))
              ) : (
                (stTableRows as typeof stateMonitoringData).map(row => (
                  <tr key={row.state} className="border-b border-border/50 hover:bg-bg-secondary transition-colors">
                    <td className="py-2.5 px-4">
                      <button onClick={() => selectState(row.state)} className="font-medium text-accent hover:underline text-left">
                        {row.state}
                      </button>
                    </td>
                    <td className={`py-2.5 px-3 text-right ${txCls(row.dataTxPercent)}`}>{row.dataTxPercent.toFixed(1)}%</td>
                    <td className={`py-2.5 px-3 text-right ${offlineCls(row.plantsOffline)}`}>{row.plantsOffline.toLocaleString()}</td>
                    <td className={`py-2.5 px-3 text-right ${alarmCls(row.activeAlarms)}`}>{row.activeAlarms.toLocaleString()}</td>
                    <td className={`py-2.5 px-3 text-right ${certCls(row.certsExpiring)}`}>{row.certsExpiring}</td>
                    <td className={`py-2.5 px-3 text-right ${certCls(row.firmwareOutdated)}`}>{row.firmwareOutdated}</td>
                  </tr>
                ))
              )}
              {stSelectedState && stTableRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-xs text-text-tertiary">No district data for {stSelectedState}.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── DISCOM-wise compliance table ── */}
      <div className="bg-white rounded-card border border-border shadow-card overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center gap-3">
          <SectionDivider
            title={dcSelectedDISCOM ? `Circles — ${dcSelectedDISCOM}` : 'DISCOM-wise Compliance'}
            subtitle="Default sort: data transmission % ascending (worst first)"
          />
          <div className="flex-1" />
          {dcSelectedDISCOM ? (
            <div className="flex items-center gap-1 text-xs text-text-secondary">
              <button onClick={() => selectDISCOM(null)} className="text-accent hover:underline font-medium">All DISCOMs</button>
              <ChevronRight size={11} className="text-text-tertiary" />
              <span className="font-medium text-text-primary">{dcSelectedDISCOM}</span>
            </div>
          ) : (
            <select
              value=""
              onChange={e => { if (e.target.value) selectDISCOM(e.target.value); }}
              className="text-xs border border-border rounded-input px-2.5 py-1.5 bg-white focus:outline-none focus:border-accent"
            >
              <option value="">All DISCOMs</option>
              {discomNames.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg-secondary border-b border-border">
              <tr>
                <SortTh label={dcSelectedDISCOM ? 'Circle' : 'DISCOM'} col={dcNameCol} sortCol={dcSort} sortDir={dcDir} onClick={toggleDcSort} />
                {!dcSelectedDISCOM && <th className="text-left py-2.5 px-3 text-text-secondary font-semibold">State</th>}
                <SortTh label="Data Tx %"         col="dataTxPercent"    sortCol={dcSort} sortDir={dcDir} onClick={toggleDcSort} right />
                <SortTh label="Plants Offline"    col="plantsOffline"    sortCol={dcSort} sortDir={dcDir} onClick={toggleDcSort} right />
                <SortTh label="Active Alarms"     col="activeAlarms"     sortCol={dcSort} sortDir={dcDir} onClick={toggleDcSort} right />
                <SortTh label="Certs Expiring"    col="certsExpiring"    sortCol={dcSort} sortDir={dcDir} onClick={toggleDcSort} right />
                <SortTh label="Firmware Outdated" col="firmwareOutdated" sortCol={dcSort} sortDir={dcDir} onClick={toggleDcSort} right />
              </tr>
            </thead>
            <tbody>
              {dcSelectedDISCOM ? (
                (dcTableRows as typeof circleMonitoringData).map(row => (
                  <tr key={row.circle} className="border-b border-border/50 hover:bg-bg-secondary transition-colors">
                    <td className="py-2.5 px-4 font-medium text-text-primary">{row.circle}</td>
                    <td className={`py-2.5 px-3 text-right ${txCls(row.dataTxPercent)}`}>{row.dataTxPercent.toFixed(1)}%</td>
                    <td className={`py-2.5 px-3 text-right ${offlineCls(row.plantsOffline)}`}>{row.plantsOffline.toLocaleString()}</td>
                    <td className={`py-2.5 px-3 text-right ${alarmCls(row.activeAlarms)}`}>{row.activeAlarms.toLocaleString()}</td>
                    <td className={`py-2.5 px-3 text-right ${certCls(row.certsExpiring)}`}>{row.certsExpiring}</td>
                    <td className={`py-2.5 px-3 text-right ${certCls(row.firmwareOutdated)}`}>{row.firmwareOutdated}</td>
                  </tr>
                ))
              ) : (
                (dcTableRows as typeof discomMonitoringData).map(row => (
                  <tr key={row.discom} className="border-b border-border/50 hover:bg-bg-secondary transition-colors">
                    <td className="py-2.5 px-4">
                      <button onClick={() => selectDISCOM(row.discom)} className="font-medium text-accent hover:underline text-left">
                        {row.discom}
                      </button>
                    </td>
                    <td className="py-2.5 px-3 text-text-secondary">{row.state}</td>
                    <td className={`py-2.5 px-3 text-right ${txCls(row.dataTxPercent)}`}>{row.dataTxPercent.toFixed(1)}%</td>
                    <td className={`py-2.5 px-3 text-right ${offlineCls(row.plantsOffline)}`}>{row.plantsOffline.toLocaleString()}</td>
                    <td className={`py-2.5 px-3 text-right ${alarmCls(row.activeAlarms)}`}>{row.activeAlarms.toLocaleString()}</td>
                    <td className={`py-2.5 px-3 text-right ${certCls(row.certsExpiring)}`}>{row.certsExpiring}</td>
                    <td className={`py-2.5 px-3 text-right ${certCls(row.firmwareOutdated)}`}>{row.firmwareOutdated}</td>
                  </tr>
                ))
              )}
              {dcSelectedDISCOM && dcTableRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-xs text-text-tertiary">No circle data for {dcSelectedDISCOM}.</td>
                </tr>
              )}
              {!dcSelectedDISCOM && dcTableRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-xs text-text-tertiary">No DISCOMs match the filter.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
